import { useMemo } from "react";
import { type AmazonProfile, toAmazonProfiles } from "./useAmazonProfiles";
import { css } from "../../../../styled-system/css";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { apiClient } from "../../../zodios/client";
import { Accordion } from "../../core/basic/Accordion";
import { Button } from "../../core/basic/Button";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { TableErrorWrapper } from "../../table/TableErrorWrapper";
import { HStack } from "../../../../styled-system/jsx";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";

export const useMissingTrellisAdsProfiles = () => {
  return useQuery({
    queryKey: [{ entity: "missingTrellisProfiles" }],
    queryFn: async () => await apiClient.getMissingAdvertisingProfiles(),
  });
};

const columnHelper = createAppColumnHelper<AmazonProfile>();

const globalFilterFn = commaSearchFilterFn<AmazonProfile>((profile) =>
  [profile.profile_name, profile.marketplaces, profile.profile_type, profile.entity_id].join(" "),
);

function MissingProfilesTable({ data, persistKey }: { data: AmazonProfile[]; persistKey: string }) {
  const { t } = useTranslation(["onboarding", "common"]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey,
    initialState: { sorting: [{ id: "profile_name", desc: false }] },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("profile_name", {
        header: t(($) => $.onboarding["Profile Name"]),
        size: 250,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((profile) => profile.marketplaces.join(", "), {
        id: "marketplaces",
        header: t(($) => $.onboarding["Profile Marketplaces"]),
        size: 180,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("profile_type", {
        header: t(($) => $.onboarding["Profile Type"]),
        size: 130,
        enableResizing: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) => snakeCaseToSentenceCase(getValue()),
      }),
    ],
    [t],
  );

  const table = useClientTable({
    data,
    columns,
    globalFilterFn,
    initialState,
    state,
    ...setters,
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
}

/** Staff-only: profiles Amazon returns that Trellis cannot manage, one section per reason. */
export function MissingProfilesSections() {
  const { t } = useTranslation(["onboarding"]);
  const { data: missingProfilesData, error } = useMissingTrellisAdsProfiles();

  const profilesMissingWriteAccess = useMemo(
    () => toAmazonProfiles(missingProfilesData?.profiles_missing_write_access),
    [missingProfilesData],
  );
  const profilesWithInvalidPaymentMethods = useMemo(
    () => toAmazonProfiles(missingProfilesData?.profiles_with_invalid_payment_methods),
    [missingProfilesData],
  );

  // A section shows only when it has profiles, or to say the query failed, so nothing renders until the
  // query answers.
  const errorMessage = t(($) => $.onboarding["Error loading profiles"]);
  return (
    <>
      {(!!error || profilesMissingWriteAccess.length > 0) && (
        <Accordion title={t(($) => $.onboarding["Profiles Missing Write Access"])}>
          <TableErrorWrapper error={error} userMessage={errorMessage}>
            <MissingProfilesTable data={profilesMissingWriteAccess} persistKey="profilesMissingWriteAccessTable" />
          </TableErrorWrapper>
        </Accordion>
      )}
      {(!!error || profilesWithInvalidPaymentMethods.length > 0) && (
        <Accordion title={t(($) => $.onboarding["Profiles With Invalid Payment Methods"])}>
          <TableErrorWrapper error={error} userMessage={errorMessage}>
            <MissingProfilesTable
              data={profilesWithInvalidPaymentMethods}
              persistKey="profilesWithInvalidPaymentMethodsTable"
            />
          </TableErrorWrapper>
        </Accordion>
      )}
    </>
  );
}
